/**
 * Top Games Hook
 * Now integrates with the sections store instead of just returning raw API data
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SECTION_NAMES } from '@repo/common';
import { fetchTopGames } from 'core/api/endpoints/games.api';
import type { TopGamesGame } from 'types/api/api.types';
import { useAppStore, useIsIdle } from 'store/app/appStore';
import { updateTopGames } from '../services/topGamesService';
const TOP_GAMES_QUERY_KEY = ['TopGames'];

/**
 * Raw API data hook - for internal use only
 */
const useTopGamesAPI = () => {
  const isIdle = useIsIdle();
  return useQuery<TopGamesGame[], Error>({
    queryKey: TOP_GAMES_QUERY_KEY,
    queryFn: fetchTopGames,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !isIdle
  });
};

/**
 * Main hook for top games functionality
 * Returns the games from the sections store and handles automatic updates
 */
export const useTopGames = () => {
  const sections = useAppStore(state => state.sections);
  const supportedGames = useAppStore(state => state.supportedGames);
  
  // Get top section from store
  const TopGamesSection = useMemo(
    () => sections.find(s => s.name === SECTION_NAMES.TOP)
    , [sections]
  );
  
  // Query API data for comparison/updates
  const { data } = useTopGamesAPI();
  
  // Auto-update top games when supported games are loaded
  useEffect(() => {
    if (TopGamesSection && Object.keys(supportedGames).length > 0 && data) {
      // Check if section needs updating (has no games or API has new data)
      const needsUpdate = !TopGamesSection?.games?.length || data?.length > 0;
      
      if (needsUpdate) {
        updateTopGames(data);
      }
    }
  }, [TopGamesSection, supportedGames, data]);
};
